import { useState, useMemo, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import { analytics } from "@/lib/analytics";
import { devLog } from "@/lib/devLog";
import { useSessionState } from "@/hooks/useSessionState";
import { Logo } from "@/components/Logo";
import { GradientActionButton, BUTTON_PALETTES } from "@/components/GradientActionButton";
import { RealMovieCard } from "@/components/RealMovieCard";
import { AIEngineSearchBox } from "@/components/AIEngineSearchBox";
import { QuickDiscovery, DiscoveryParams } from "@/components/QuickDiscovery";
import { EnhancedMovieCard, CollectionInfo } from "@/components/EnhancedMovieCard";
import { SimilarMovies } from "@/components/SimilarMovies";

import { PostResultsUpgradeBanner } from "@/components/PostResultsUpgradeBanner";
import { SurpriseMePicker } from "@/components/SurpriseMePicker";
import { MoodAnalyzer, ParsedMoodFilters } from "@/utils/moodAnalyzer";


import { UsageIndicator } from "@/components/UsageIndicator";
import { SEOHead } from "@/components/SEOHead";
import { Footer } from "@/components/Footer";
import { AIModelIndicator } from "@/components/AIModelIndicator";
import { PersonalizedHero } from "@/components/PersonalizedHero";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { FeaturePreviewCard } from "@/components/FeaturePreviewCard";
import { PickFeedbackBar } from "@/components/PickFeedbackBar";
import { FeatureTooltips } from "@/components/FeatureTooltips";

import { SearchInsights } from "@/components/SearchInsights";
import { QuickFeaturesList } from "@/components/QuickFeaturesList";
import { useUserRole } from "@/hooks/useUserRole";
import { usePremiumStatus } from "@/hooks/usePremiumStatus";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Crown, Heart, Sparkles, TrendingUp, Zap, Filter, LogIn, Keyboard, Grid3x3, List, ChevronLeft, ChevronRight, RefreshCw, User, Share2, Gem, Dices, Users, Bookmark, Eye, CheckCircle2, MessageSquare, CalendarClock, Home, ShieldCheck, Search } from "lucide-react";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationPrevious, PaginationNext, PaginationEllipsis } from "@/components/ui/pagination";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Link, useSearchParams } from "react-router-dom";
import GuestShowcase from "@/components/GuestShowcase";
import { requestTicket } from "@/lib/guest";
import { openManualSearch } from "@/lib/manualSearch";
import { CinemaBackground } from "@/components/CinemaBackground";
import { InteractiveStars } from "@/components/InteractiveStars";
import { InlineFeedback } from "@/components/InlineFeedback";
import { NowPlayingMarquee } from "@/components/NowPlayingMarquee";

// Heavy, tab-scoped surfaces (charts, PDF export, analysis, wheel, billing).
// Split out of the initial bundle so the first paint on a phone only parses
// what the visible tab needs.
const TonightTab = lazy(() => import("@/components/home/TonightTab").then((m) => ({ default: m.TonightTab })));
const CinemaMarathonHome = lazy(() => import("@/components/watch-planner/CinemaMarathonHome").then((m) => ({ default: m.CinemaMarathonHome })));
const ShareWatchlist = lazy(() => import("@/components/ShareWatchlist").then((m) => ({ default: m.ShareWatchlist })));
const EnhancedSpinWheel = lazy(() => import("@/components/EnhancedSpinWheel").then((m) => ({ default: m.EnhancedSpinWheel })));
const CinemaVault = lazy(() => import("@/components/CinemaVault").then((m) => ({ default: m.CinemaVault })));
const MovieAnalysis = lazy(() => import("@/components/MovieAnalysis").then((m) => ({ default: m.MovieAnalysis })));
const SubscriptionManager = lazy(() => import("@/components/SubscriptionManager").then((m) => ({ default: m.SubscriptionManager })));




// Convert era slider (0-100) to year range, matching quick-discover logic
function getEraYearRange(era: number): { min?: number; max?: number } {
  const currentYear = new Date().getFullYear();
  if (era >= 80) return { min: currentYear - 1, max: currentYear };
  if (era >= 60) return { min: 2020, max: currentYear };
  if (era >= 40) return { min: 2010, max: 2019 };
  if (era >= 20) return { min: 2000, max: 2009 };
  return { min: 1970, max: 1999 };
}

interface FilterState {
  genres: string[];
  yearRange: string;
  rating: string;
  language: string;
  mediaType: string;
  runtime: string;
}

interface TMDBResult {
  id: number;
  title: string;
  year: number | null;
  overview: string;
  poster: string | null;
  backdrop: string | null;
  rating: number;
  vote_count: number;
  genre_ids: number[];
  genres?: string[];
  language: string;
  media_type: 'movie' | 'tv';
  tmdb_id: number;
  certification?: string;
  ai_reason?: string;
  ai_match_score?: number;
}

interface WatchlistItem {
  id: number;
  title: string;
  year: number | null;
  poster: string | null;
  media_type: 'movie' | 'tv';
  addedAt: string;
  watchStatus: 'saved' | 'watching' | 'watched';
}

interface FavoriteWatchlistRow {
  tmdb_id: number;
  title: string;
  release_date: string | number | null;
  poster_path: string | null;
  media_type: string;
  added_at: string;
  watch_status?: string | null;
}

type LastSearchParams = {
  query: string;
  mediaType: 'movie' | 'tv' | 'all';
  moods: string[];
  minYear?: number;
  maxYear?: number;
  genres: string[];
  runtime?: { min: number; max: number };
  language?: string;
  searchType: 'ai-search' | 'quick-discover';
  obscurity?: number;
  era?: number;
  keywords?: string;
};

interface IndexSnapshotPayload {
  activeTab: string;
  searchQuery: string;
  searchMediaType: 'movie' | 'tv' | 'all';
  filters: FilterState;
  movies: TMDBResult[];
  currentPage: number;
  totalPages: number;
  viewMode: 'grid' | 'compact';
  shownMovieIds: number[];
  subscriptionOpen: boolean;
  aiModelInfo: unknown;
  searchCount: number;
  lastSearchParams: LastSearchParams | null;
}

interface IndexSnapshotEntry {
  v: 1;
  t: number;
  data: IndexSnapshotPayload;
}

const INDEX_SNAPSHOT_KEY = 'idx_state_snapshot_v1';
const DISCOVER_BROWSE_CACHE_KEY = 'cache_discover_browse_v1';
const DISCOVER_BROWSE_CACHE_TTL = 10 * 60 * 1000;

type DiscoverBrowseCacheEntry = {
  data: TMDBResult[];
  ts: number;
};

let inMemoryDiscoverBrowseCache: DiscoverBrowseCacheEntry | null = null;
let discoverBrowseFetchPromise: Promise<TMDBResult[] | null> | null = null;

const readDiscoverBrowseCache = (): TMDBResult[] | null => {
  const parseEntry = (raw: string | null): DiscoverBrowseCacheEntry | null => {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (
        parsed &&
        typeof parsed === 'object' &&
        Array.isArray(parsed.data) &&
        typeof parsed.ts === 'number'
      ) {
        return parsed;
      }
    } catch {
      // ignore malformed cache
    }
    return null;
  };

  if (inMemoryDiscoverBrowseCache) {
    return inMemoryDiscoverBrowseCache.data;
  }

  try {
    const sessionEntry = parseEntry(sessionStorage.getItem(DISCOVER_BROWSE_CACHE_KEY));
    const localEntry = parseEntry(localStorage.getItem(DISCOVER_BROWSE_CACHE_KEY));
    const latest = !sessionEntry
      ? localEntry
      : !localEntry
      ? sessionEntry
      : sessionEntry.ts >= localEntry.ts
      ? sessionEntry
      : localEntry;

    if (!latest) return null;
    if (Date.now() - latest.ts > DISCOVER_BROWSE_CACHE_TTL) return null;
    inMemoryDiscoverBrowseCache = latest;
    return latest.data;
  } catch {
    return null;
  }
};

const writeDiscoverBrowseCache = (data: TMDBResult[]) => {
  const entry: DiscoverBrowseCacheEntry = { data, ts: Date.now() };
  inMemoryDiscoverBrowseCache = entry;

  try {
    const serialized = JSON.stringify(entry);
    try {
      sessionStorage.setItem(DISCOVER_BROWSE_CACHE_KEY, serialized);
    } catch {}
    try {
      localStorage.setItem(DISCOVER_BROWSE_CACHE_KEY, serialized);
    } catch {}
  } catch {
    // ignore serialization failures
  }
};

const aiSearchBaseFilters = (mediaType: 'movie' | 'tv' | 'all'): FilterState => ({
  genres: [],
  yearRange: 'all',
  rating: 'all',
  language: 'all',
  mediaType,
  runtime: 'all',
});

const VALID_TABS = ['tonight', 'discover', 'analyze', 'vault', 'random'];
// Backward-compat: map old tab slugs to new ones
const LEGACY_TAB_MAP: Record<string, string> = { ai: 'vault', wheel: 'random' };

// Module-level guard: survives component remounts caused by brief auth state flickers
let moduleInitialDataFetchedForUser: string | null = null;

const parseWatchlistYear = (releaseDate: string | number | null | undefined): number | null => {
  if (typeof releaseDate === 'number') return releaseDate;
  if (typeof releaseDate === 'string') {
    const parsedYear = parseInt(releaseDate, 10);
    return Number.isNaN(parsedYear) ? null : parsedYear;
  }

  return null;
};

const mapFavoriteToWatchlistItem = (item: FavoriteWatchlistRow): WatchlistItem => ({
  id: item.tmdb_id,
  title: item.title,
  year: parseWatchlistYear(item.release_date),
  poster: item.poster_path,
  media_type: item.media_type as 'movie' | 'tv',
  addedAt: item.added_at,
  watchStatus: ((item.watch_status as 'saved' | 'watching' | 'watched' | null | undefined) ?? 'saved'),
});

/** Quiet placeholder shown while a tab's JS chunk downloads. */
const TabChunkFallback = () => (
  <div className="min-h-[220px] flex items-center justify-center" aria-busy="true">
    <span className="text-[11px] font-bold uppercase tracking-[0.28em] text-muted-foreground">
      Loading…
    </span>
  </div>
);

const Index = () => {
  const { toast } = useToast();
  const { user, authReady, signOut } = useAuth();
  // unread messages now surfaced via the global MarqueeDock
  const userId = user?.id ?? null;
  const { isOwner, loading: roleLoading } = useUserRole(user?.id);
  const { isPremium, loading: premiumLoading, refetch: refetchPremium } = usePremiumStatus(user?.id);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const guestPrefilledQueryRef = useRef<string | null>(null);
  /**
   * Guests get the doors-and-curtains landing page first. Once they choose to
   * look around, the real theatre stays open for the rest of the session (tab
   * links drop the `?browse=1`, so the choice is remembered, not URL-bound).
   */
  const [guestBrowsing, setGuestBrowsing] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return sessionStorage.getItem("swiftix_guest_browsing") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (searchParams.get("browse") !== "1") return;
    try { sessionStorage.setItem("swiftix_guest_browsing", "1"); } catch { /* private mode */ }
    setGuestBrowsing(true);
    const next = new URLSearchParams(searchParams);
    next.delete("browse");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const showGuestLanding = authReady && !user && !guestBrowsing;
  const [searchQuery, setSearchQuery] = useSessionState("idx_searchQuery", "");
  const [searchMediaType, setSearchMediaType] = useSessionState<'movie' | 'tv' | 'all'>("idx_searchMediaType", 'movie');
  const [isSearching, setIsSearching] = useState(false);




  const rawUrlTab = searchParams.get('tab');
  const urlTab = rawUrlTab && LEGACY_TAB_MAP[rawUrlTab] ? LEGACY_TAB_MAP[rawUrlTab] : rawUrlTab;
  const storedTabRaw = (() => {
    try {
      return sessionStorage.getItem('idx_activeTab') ?? localStorage.getItem('idx_activeTab');
    } catch {
      return null;
    }
  })();
  const storedTab = storedTabRaw && LEGACY_TAB_MAP[storedTabRaw] ? LEGACY_TAB_MAP[storedTabRaw] : storedTabRaw;
  const activeTab = VALID_TABS.includes(urlTab || '')
    ? urlTab!
    : (VALID_TABS.includes(storedTab || '') ? storedTab! : 'tonight');

  const setActiveTab = useCallback((tab: string) => {
    try {
      try {
        sessionStorage.setItem('idx_activeTab', tab);
      } catch {}
      try {
        localStorage.setItem('idx_activeTab', tab);
      } catch {}
    } catch {}
    setSearchParams({ tab }, { replace: true });
  }, [setSearchParams]);

  const [filters, setFilters] = useSessionState<FilterState>("idx_filters", {
    genres: [],
    yearRange: "all",
    rating: "all",
    language: "all",
    mediaType: "all",
    runtime: "all"
  });
  const [movies, setMovies] = useSessionState<TMDBResult[]>("idx_movies", []);
  const [watchlist, setWatchlist] = useState<number[]>([]);
  const [watchlistItems, setWatchlistItems] = useState<WatchlistItem[]>([]);
  // Free members get 10 random picks per day; VIP is unlimited.
  const FREE_DAILY_PICKS = 10;
  const spinKey = `spin_used_${new Date().toISOString().slice(0, 10)}`;
  const [freeSpinsLeft, setFreeSpinsLeft] = useState(() => {
    try {
      const used = parseInt(localStorage.getItem(spinKey) || '0', 10);
      return Math.max(0, FREE_DAILY_PICKS - (Number.isFinite(used) ? used : 0));
    } catch { return FREE_DAILY_PICKS; }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useSessionState("idx_currentPage", 1);
  const [totalPages, setTotalPages] = useSessionState("idx_totalPages", 1);
  const [viewMode, setViewMode] = useSessionState<'grid' | 'compact'>("idx_viewMode", 'grid');
  const [shownMovieIds, setShownMovieIds] = useSessionState<number[]>("idx_shownMovieIds", []);
  const [subscriptionOpen, setSubscriptionOpen] = useSessionState<boolean>("idx_subscriptionOpen", false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedAIModel, setSelectedAIModel] = useState(() => localStorage.getItem('owner_ai_model') || 'huggingface/meta-llama/Llama-3.2-3B-Instruct');
  const [shareOpen, setShareOpen] = useState(false);
  const [aiModelInfo, setAiModelInfo] = useSessionState<any>("idx_aiModelInfo", null);
  const [searchCount, setSearchCount] = useSessionState("idx_searchCount", 0);
  const [userName, setUserName] = useState<string>('');
  const [userCollections, setUserCollections] = useState<CollectionInfo[]>([]);
  const [lastSearchParams, setLastSearchParams] = useSessionState<LastSearchParams | null>("idx_lastSearchParams", null);
  const [snapshotHydrated, setSnapshotHydrated] = useState(false);

  const readLatestSnapshot = useCallback((): IndexSnapshotEntry | null => {
    const parseSnapshot = (raw: string | null): IndexSnapshotEntry | null => {
      if (!raw) return null;
      try {
        const parsed = JSON.parse(raw) as IndexSnapshotEntry;
        if (
          parsed &&
          parsed.v === 1 &&
          typeof parsed.t === 'number' &&
          parsed.data &&
          typeof parsed.data === 'object'
        ) {
          return parsed;
        }
      } catch {
        // ignore malformed entry
      }
      return null;
    };

    try {
      const sessionSnapshot = parseSnapshot(sessionStorage.getItem(INDEX_SNAPSHOT_KEY));
      const localSnapshot = parseSnapshot(localStorage.getItem(INDEX_SNAPSHOT_KEY));

      if (!sessionSnapshot) return localSnapshot;
      if (!localSnapshot) return sessionSnapshot;
      return sessionSnapshot.t >= localSnapshot.t ? sessionSnapshot : localSnapshot;
    } catch {
      return null;
    }
  }, []);

  const readPersistedValue = useCallback(<T,>(key: string): T | null => {
    const parseEntry = (raw: string | null): { value: T; timestamp: number } | null => {
      if (!raw) return null;

      try {
        const parsed = JSON.parse(raw);
        if (
          parsed &&
          typeof parsed === 'object' &&
          'v' in parsed &&
          't' in parsed &&
          typeof parsed.t === 'number'
        ) {
          return { value: parsed.v as T, timestamp: parsed.t as number };
        }

        // Backward compatibility with previously stored plain values.
        return { value: parsed as T, timestamp: 0 };
      } catch {
        return null;
      }
    };

    try {
      const sessionEntry = parseEntry(sessionStorage.getItem(key));
      const localEntry = parseEntry(localStorage.getItem(key));

      if (!sessionEntry && !localEntry) return null;
      if (!localEntry) return sessionEntry!.value;
      if (!sessionEntry) return localEntry.value;

      return sessionEntry.timestamp >= localEntry.timestamp
        ? sessionEntry.value
        : localEntry.value;
    } catch {
      return null;
    }
  }, []);

  const restoreFromSnapshotRef = useRef<() => void>(() => {});

  // Keep the ref always pointing to the latest closure
  restoreFromSnapshotRef.current = () => {
    const snapshot = readLatestSnapshot();
    if (!snapshot) return;

    const data = snapshot.data;
    

    if (!searchParams.has('tab') && VALID_TABS.includes(data.activeTab) && data.activeTab !== activeTab) {
      setActiveTab(data.activeTab);
    }
    if (data.searchQuery) setSearchQuery(data.searchQuery);
    if (data.searchMediaType) setSearchMediaType(data.searchMediaType);
    if (data.filters) setFilters(data.filters);
    if (data.movies?.length > 0) setMovies(data.movies);
    if (data.currentPage) setCurrentPage(data.currentPage);
    if (data.totalPages) setTotalPages(data.totalPages);
    if (data.viewMode) setViewMode(data.viewMode);
    if (data.shownMovieIds?.length > 0) setShownMovieIds(data.shownMovieIds);
    setSubscriptionOpen(data.subscriptionOpen);
    if (data.aiModelInfo) setAiModelInfo(data.aiModelInfo);
    if (data.searchCount) setSearchCount(data.searchCount);
    if (data.lastSearchParams) setLastSearchParams(data.lastSearchParams);
  };

  // Hydrate from snapshot on every fresh mount (handles Chrome tab discard)
  useEffect(() => {
    restoreFromSnapshotRef.current();
    setSnapshotHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pageshow (bfcache) + visibilitychange (Chrome tab discard) listeners
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        restoreFromSnapshotRef.current();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      // If current React state looks empty but snapshot has data, restore it
      // (This catches Chrome tab discard where the page silently reloaded)
      const snapshot = readLatestSnapshot();
      if (!snapshot) return;
      const data = snapshot.data;
      const currentSearchQuery = readPersistedValue<string>('idx_searchQuery') ?? '';
      const currentMovies = readPersistedValue<TMDBResult[]>('idx_movies') ?? [];
      // If snapshot had a search but current state is empty, force restore
      if (data.searchQuery && !currentSearchQuery.trim() && data.movies?.length > 0 && currentMovies.length === 0) {
        restoreFromSnapshotRef.current();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [readLatestSnapshot, readPersistedValue]);

  useEffect(() => {
    if (!snapshotHydrated) {
      return;
    }

    const snapshot: IndexSnapshotEntry = {
      v: 1,
      t: Date.now(),
      data: {
        activeTab,
        searchQuery,
        searchMediaType,
        filters,
        movies,
        currentPage,
        totalPages,
        viewMode,
        shownMovieIds,
        subscriptionOpen,
        aiModelInfo,
        searchCount,
        lastSearchParams,
      },
    };

    let idleId: number | null = null;
    const timer = window.setTimeout(() => {
      const persist = () => {
        try {
          const serialized = JSON.stringify(snapshot);
          try { sessionStorage.setItem(INDEX_SNAPSHOT_KEY, serialized); } catch {}
          try { localStorage.setItem(INDEX_SNAPSHOT_KEY, serialized); } catch {}
        } catch {
          // Snapshot persistence must never block interaction.
        }
      };
      if ("requestIdleCallback" in window) {
        idleId = window.requestIdleCallback(persist, { timeout: 1500 });
      } else {
        persist();
      }
    }, 500);

    return () => {
      window.clearTimeout(timer);
      if (idleId !== null && "cancelIdleCallback" in window) window.cancelIdleCallback(idleId);
    };
  }, [
    activeTab,
    aiModelInfo,
    currentPage,
    filters,
    lastSearchParams,
    movies,
    searchCount,
    searchMediaType,
    searchQuery,
    shownMovieIds,
    subscriptionOpen,
    totalPages,
    viewMode,
    snapshotHydrated,
  ]);

  // Persist owner model selection
  useEffect(() => {
    try {
      localStorage.setItem('owner_ai_model', selectedAIModel);
    } catch {}
  }, [selectedAIModel]);

  // Tutorial is now mounted at App level

  // Load user profile and collections for personalization
  useEffect(() => {
    const loadUserData = async () => {
      if (!userId) return;
      
      const [profileResult, collectionsResult] = await Promise.all([
        supabase.from('profiles').select('full_name').eq('user_id', userId).single(),
        isPremium
          ? supabase.from('custom_lists').select('id, name').eq('user_id', userId).order('name')
          : Promise.resolve({ data: [] as { id: string; name: string }[] }),
      ]);
      const profile = profileResult.data;
      
      if (profile?.full_name) {
        setUserName(profile.full_name);
      }

      // Load collections once for all EnhancedMovieCard instances.
      setUserCollections(collectionsResult.data || []);
    };
    
    loadUserData();
  }, [userId, isPremium]);

  const applyWatchlistState = useCallback((watchlistData: FavoriteWatchlistRow[]) => {
    setWatchlist(watchlistData.map((favorite) => favorite.tmdb_id));
    setWatchlistItems(watchlistData.map(mapFavoriteToWatchlistItem));
  }, []);

  const initialDataFetchedRef = useRef(false);

  // Load initial data
  useEffect(() => {
    let cancelled = false;
    const loadInitialData = async () => {
      // Guests browse the real catalogue too: the browse fetch runs for
      // everyone, only the watchlist load is member-only.
      const cacheKey = userId ?? 'guest';
      const shouldFetchBrowseData = !initialDataFetchedRef.current && moduleInitialDataFetchedForUser !== cacheKey;

      // Browse fetch and watchlist fetch are isolated so a browse-fetch
      // failure cannot short-circuit the watchlist load (which would leave
      // TonightTab's shelf empty even though the marquee counts show items).
      try {
        if (shouldFetchBrowseData) {
          initialDataFetchedRef.current = true;
          moduleInitialDataFetchedForUser = cacheKey;


          // Read directly from storage instead of stale closure state
          const persistedMovies = readPersistedValue<TMDBResult[]>('idx_movies') ?? [];
          const persistedSearchQuery = readPersistedValue<string>('idx_searchQuery') ?? '';
          const persistedLastSearchParams = readPersistedValue<LastSearchParams | null>('idx_lastSearchParams');
          const hasRestorableState = !!persistedSearchQuery.trim() || persistedMovies.length > 0 || !!persistedLastSearchParams;

          // Only fetch default trending content when there is no restorable state.
          if (!hasRestorableState) {
            const cachedBrowseMovies = readDiscoverBrowseCache();
            if (cachedBrowseMovies !== null) {
              setMovies(cachedBrowseMovies);
            } else {
              setIsLoading(true);
              if (!discoverBrowseFetchPromise) {
                discoverBrowseFetchPromise = (async (): Promise<TMDBResult[] | null> => {
                  const initialFilters = {
                    ...filters,
                    mediaType: 'all'
                  };

                  const { data, error } = await supabase.functions.invoke('tmdb-search', {
                    body: {
                      query: '',
                      page: 1,
                      filters: initialFilters,
                      userId
                    }
                  });

                  if (error) {
                    if (error.message?.includes('Unauthorized') || error.message?.includes('401')) {
                      devLog('Auth required for TMDB search');
                      return null;
                    }
                    throw error;
                  }

                  const initialResults = data.results || [];
                  writeDiscoverBrowseCache(initialResults);
                  return initialResults;
                })();
              }

              const initialResults = await discoverBrowseFetchPromise;
              // Not gated on `cancelled`: this is shared browse content, and an
              // auth-driven effect re-run must not swallow the only fetch.
              if (initialResults !== null) {
                setMovies(initialResults);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error loading browse data:', error);
        toast({
          title: "Error",
          description: "Failed to load movies. Please try again.",
          variant: "destructive"
        });
      } finally {
        discoverBrowseFetchPromise = null;
      }

      if (!userId) {
        if (!cancelled) setIsLoading(false);
        return;
      }

      // Always refresh watchlist data for current user, regardless of browse
      // fetch outcome (lightweight DB call, no TMDB dependency).
      try {
        const { data: watchlistData, error: watchlistError } = await supabase
          .from('user_favorites')
          .select('*')
          .eq('user_id', userId)
          .order('added_at', { ascending: false });

        if (watchlistError) throw watchlistError;
        if (cancelled) return;
        applyWatchlistState((watchlistData || []) as FavoriteWatchlistRow[]);
      } catch (error) {
        console.error('Error loading watchlist:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }

    };

    if (authReady && snapshotHydrated) {
      loadInitialData();
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, authReady, snapshotHydrated, applyWatchlistState]);

  // Real-time subscription for watchlist updates (debounced to prevent churn on reconnect)
  useEffect(() => {
    if (!userId) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const channel = supabase
      .channel('watchlist-main-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_favorites',
          filter: `user_id=eq.${userId}`
        },
        () => {
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = setTimeout(async () => {
            try {
              const { data: watchlistData, error } = await supabase
                .from('user_favorites')
                .select('*')
                .eq('user_id', userId)
                .order('added_at', { ascending: false });

              if (error) throw error;
              applyWatchlistState((watchlistData || []) as FavoriteWatchlistRow[]);
            } catch (error) {
              console.error('Error refreshing watchlist:', error);
            }
          }, 500);
        }
      )
      .subscribe();

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, [userId, applyWatchlistState]);

  // Search and filter movies
  const searchMovies = async (query = searchQuery, newFilters = filters, page = 1) => {
    setIsLoading(true);
    try {
      const adjustedFilters = { ...newFilters };


      const { data, error } = await supabase.functions.invoke('tmdb-search', {
        body: { 
          query, 
          page, 
          filters: adjustedFilters,
          userId: user?.id 
        }
      });

      if (error) throw error;
      setMovies(data.results || []);
      setCurrentPage(page);
      setTotalPages(Math.min(data.total_pages || 1, 500)); // TMDB limits to 500 pages
    } catch (error) {
      console.error('Error searching movies:', error);
      toast({
        title: "Search Error",
        description: "Failed to search movies. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    searchMovies(searchQuery, filters, newPage);
  };

  // Watchlist IDs for the wheel component
  const watchlistTmdbIds = watchlist;

  // Auto-run a guest-prefilled search after sign-in (?q= from /auth)
  useEffect(() => {
    if (!user) return;
    const q = searchParams.get('q');
    if (!q || guestPrefilledQueryRef.current === q) return;
    guestPrefilledQueryRef.current = q;
    void handleSearch(q, 'movie');
    const next = new URLSearchParams(searchParams);
    next.delete('q');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, searchParams]);

  const handleSearch = async (query: string, mediaType: 'movie' | 'tv' | 'all' = 'movie', aiModel?: string) => {
    // Searching runs on paid AI reels — ticket holders only.
    if (!user) {
      requestTicket('use Quick Discovery');
      return;
    }
    const isRefresh = typeof aiModel === 'boolean' ? aiModel : false;
    const selectedModel = typeof aiModel === 'string' ? aiModel : undefined;
    // Sanitize and validate input
    const sanitizedQuery = query.trim().slice(0, 500); // Limit to 500 chars
    if (!sanitizedQuery && !isRefresh) {
      await searchMovies('', filters);
      setShownMovieIds([]);
      return;
    }
    
    // Track search count for personalization
    setSearchCount(prev => prev + 1);
    analytics.track('ai_search_performed', { query: sanitizedQuery, media_type: mediaType });
    
    setSearchQuery(sanitizedQuery);
    setSearchMediaType(mediaType); // Store media type for refresh
    
    // Clear shown IDs for new searches (not refreshes) to get fresh results
    if (!isRefresh) {
      setShownMovieIds([]);
    }
    
    setIsSearching(true);
    setIsLoading(true);
    setActiveTab('discover');

    // Import cache utilities
    const { isSimpleQuery, searchCache } = await import('@/utils/searchCache');
    
    // Check if this is a simple query (exact title search)
    const useRegularSearch = isSimpleQuery(sanitizedQuery);
    
    if (useRegularSearch) {
      devLog('📋 Simple query detected, using regular TMDB search');
      // IMPORTANT: Respect the media type selected in the AI search box.
      await searchMovies(sanitizedQuery, aiSearchBaseFilters(mediaType));
      setIsSearching(false);
      setIsLoading(false);
      toast({
        title: "Search Complete",
        description: "Showing exact title matches",
      });
      return;
    }

    // Check cache first for complex queries (skip when owner forces model)
    if (!isRefresh && !selectedModel) {
      const cachedResults = searchCache.get(sanitizedQuery, mediaType);
      if (cachedResults && cachedResults.length > 0) {
        devLog('💾 Using cached AI search results');
        setMovies(cachedResults);
        setShownMovieIds(cachedResults.map((m: TMDBResult) => m.id));
        setIsSearching(false);
        setIsLoading(false);
        toast({
          title: "💾 Cached Results",
          description: `Found ${cachedResults.length} saved recommendations (cached)`,
        });
        return;
      }
    }
    
      try {
        // Check if AI features are disabled by user preference
        const aiEnabled = localStorage.getItem('ai_features_enabled') !== 'false';
        
        if (sanitizedQuery && aiEnabled) {
          // Use AI-powered search
          const excludeIds = isRefresh ? shownMovieIds.map(id => id.toString()) : [];

          devLog('🔧 AI search payload:', {
            model: selectedModel || '(backend default)',
            mediaType,
            excludeIdsCount: excludeIds.length,
          });
          
          // Store search params for refresh/load-more
          setLastSearchParams({
            query: sanitizedQuery,
            mediaType,
            moods: [] as string[],
            genres: [] as string[],
            searchType: 'ai-search',
          });
          
          const { data, error } = await supabase.functions.invoke('ai-search', {
            body: { 
              query: sanitizedQuery,
              isPremium: isPremium,
              userId: user?.id,
              excludeIds,
              mediaType,
              preferredModel: selectedModel,
              purpose: isRefresh ? 'refresh' : 'initial'
            }
          });

        if (error) {
          console.error('AI search error:', error);
          // Fallback to regular search
          toast({
            title: "Search Unavailable",
            description: "Using standard search instead...",
          });
          await searchMovies(sanitizedQuery, aiSearchBaseFilters(mediaType));
          return;
        }

        // Check for rate limit or credit errors
        if (data.error) {
          console.warn('AI search responded with error, falling back to TMDB:', data.error);
          toast({
            title: "Smart Search Unavailable",
            description: "Falling back to standard search...",
          });
          await searchMovies(sanitizedQuery, aiSearchBaseFilters(mediaType));
          return;
        }

        // Ensure results is an array, not an object
        const resultsArray = Array.isArray(data.results) ? data.results : [];
        setMovies(resultsArray);
        setAiModelInfo(data.model_info || null); // Store model info
        
        // Track shown movie IDs for refresh functionality
        if (resultsArray.length > 0) {
          const newIds = resultsArray.map((m: TMDBResult) => m.id);
          setShownMovieIds(prev => [...prev, ...newIds]);
          
          // Cache the results for future searches (skip when owner forces model)
          if (!isRefresh && !selectedModel) {
            searchCache.set(sanitizedQuery, mediaType, resultsArray);
          }
          
          const modelBadge = data.model_info 
            ? `using ${data.model_info.name}` 
            : '';
          
          toast({
            title: isRefresh ? "✨ New Recommendations" : "✨ Search Complete",
            description: `Found ${resultsArray.length} ${isRefresh ? 'new' : ''} personalised recommendations ${modelBadge}`,
          });
        } else {
          toast({
            title: "No results found",
            description: isRefresh ? "Try a different search query" : "Try rephrasing your search",
          });
        }
      } else if (sanitizedQuery && !aiEnabled) {
          // AI disabled - use regular TMDB search
          devLog('🔇 AI features disabled, using regular TMDB search');
          await searchMovies(sanitizedQuery, aiSearchBaseFilters(mediaType));
          toast({
            title: "Search Complete",
            description: "Using standard search",
          });
      } else {
        await searchMovies('', filters);
        setShownMovieIds([]);
        setLastSearchParams(null);
      }
    } catch (error) {
      console.error('Search error:', error);
      toast({
        title: "Search Error",
        description: "Failed to search. Please try again.",
        variant: "destructive"
      });
      // Fallback to regular search
      await searchMovies(sanitizedQuery, aiSearchBaseFilters(mediaType));
    } finally {
      setIsSearching(false);
      setIsLoading(false);
    }
  };

  // Quick Discovery handler - routes text queries to AI search, mood-only to quick-discover
  const handleQuickDiscover = async (params: DiscoveryParams) => {
    if (!user) {
      requestTicket('use Quick Discovery');
      return;
    }
    analytics.track('quick_discovery_used', { moods: params.moods, media_type: params.mediaType });
    setIsSearching(true);
    setIsLoading(true);
    setActiveTab('discover');
    
    const moodLabels = params.moods.length > 0 ? params.moods.join(' + ') : 'custom';
    const hasTextQuery = !!params.textQuery?.trim();
    
    setSearchQuery(params.textQuery || `${moodLabels} mood`);
    setSearchMediaType(params.mediaType);
    
    setLastSearchParams({
      query: params.textQuery || `${moodLabels} mood`,
      mediaType: params.mediaType,
      moods: params.moods,
      genres: params.genres || [],
      runtime: params.runtime,
      language: params.language,
      searchType: hasTextQuery ? 'ai-search' : 'quick-discover',
      obscurity: params.obscurity ?? undefined,
      era: params.era ?? undefined,
      keywords: params.keywords,
    });

    try {
      // Session "already shown" ids are only used for the Show-me-more flow;
      // library exclusions are resolved server-side from the user's own data.
      const excludeIds: string[] = [];

      // KEY ROUTING LOGIC: text query → ai-search, mood-only → quick-discover
      if (hasTextQuery) {
        devLog('🧠 Quick Discover with text query → routing to AI search with all filters:', params.textQuery);
        
        // Pass ALL structured filters from Quick Discovery to ai-search
        // so moods, genres, era, runtime, etc. are used for TMDB filtering
        const yearRange = typeof params.era === 'number' ? getEraYearRange(params.era) : {};
        
        const { data, error } = await supabase.functions.invoke('ai-search', {
          body: {
            query: params.textQuery,
            isPremium: isPremium,
            userId: user?.id,
            excludeIds,
            mediaType: params.mediaType,
            purpose: params.reroll ? 'refresh' : 'initial',
            moods: params.moods,
            genres: params.genres,
            runtime: params.runtime,
            language: params.language,
            ...(yearRange.min !== undefined && { minYear: yearRange.min }),
            ...(yearRange.max !== undefined && { maxYear: yearRange.max }),
          }
        });

        if (error || data?.error) {
          console.error('AI search fallback from Quick Discover:', error || data?.error);
          toast({ title: "Smart Search Unavailable", description: "Using standard search instead...", variant: "destructive" });
          await searchMovies(params.textQuery!, aiSearchBaseFilters(params.mediaType));
        } else {
          const resultsArray = Array.isArray(data.results) ? data.results : [];
          setMovies(resultsArray);
          setAiModelInfo(data.model_info || null);
          if (resultsArray.length > 0) {
            setShownMovieIds(prev => [...prev, ...resultsArray.map((m: TMDBResult) => m.id)]);
            toast({ title: "✨ Search Complete", description: `Found ${resultsArray.length} personalised recommendations` });
          } else {
            toast({ title: "No results found", description: "Try rephrasing your search" });
          }
        }
      } else {
        devLog('🎯 Quick Discover (mood-based):', params);

        const { data, error } = await supabase.functions.invoke('quick-discover', {
          body: {
            moods: params.moods,
            mediaType: params.mediaType,
            obscurity: params.obscurity,
            era: params.era,
            excludeIds,
            isPremium: isPremium,
            reroll: params.reroll === true,
            userId: user?.id,
            keywords: params.keywords,
            genres: params.genres,
            runtime: params.runtime,
            language: params.language,
          }
        });

        if (error) {
          console.error('Quick discover error:', error);
          toast({ title: "Discovery Error", description: "Failed to find movies. Please try again.", variant: "destructive" });
          return;
        }

        const resultsArray = Array.isArray(data.results) ? data.results : [];
        setMovies(resultsArray);
        setAiModelInfo(data.model_info || null);

        const relaxations: string[] = Array.isArray(data?.meta?.relaxations) ? data.meta.relaxations : [];
        const relaxNote = relaxations.length ? ` (loose mode: ${relaxations.join('; ')})` : '';

        if (resultsArray.length > 0) {
          setShownMovieIds(prev => [...prev, ...resultsArray.map((m: TMDBResult) => m.id)]);
          const obscurityLabel = params.obscurity === null || params.obscurity === undefined
            ? 'picks'
            : params.obscurity >= 60 ? 'hidden gems' : params.obscurity >= 30 ? 'quality picks' : 'popular hits';
          toast({ title: "✨ Movies Found!", description: `Discovered ${resultsArray.length} ${obscurityLabel}${params.moods.length > 0 ? ` for "${moodLabels}"` : ''}${relaxNote}` });
        } else {
          toast({ title: "No results found", description: `Try adjusting the filters${relaxations.length ? ` — we already tried loosening: ${relaxations.join('; ')}` : ''}` });
        }
      }
    } catch (error) {
      console.error('Quick discover error:', error);
      toast({ title: "Discovery Error", description: "Failed to discover movies. Please try again.", variant: "destructive" });
    } finally {
      setIsSearching(false);
      setIsLoading(false);
    }
  };

  const handleRefreshAISearch = async () => {
    if (!user) {
      requestTicket('reshuffle the programme');
      return;
    }
    if (!searchQuery.trim()) return;
    // Reshuffling the same request is the VIP "Free re-rolls" perk.
    if (!isPremium) {
      toast({
        title: "Free re-rolls are a VIP perk",
        description: "VIP members can reshuffle the same request as often as they like without spending a search.",
      });
      return;
    }
    
    toast({
      title: "Refreshing Search",
      description: "Finding new recommendations...",
    });
    
    const query = lastSearchParams?.query || searchQuery;
    const mediaType = lastSearchParams?.mediaType || searchMediaType;
    const moods = lastSearchParams?.moods || [];
    const genres = lastSearchParams?.genres || [];
    const runtime = lastSearchParams?.runtime;
    const language = lastSearchParams?.language;
    
    setIsSearching(true);
    setIsLoading(true);

    try {
      const excludeIds = shownMovieIds.map(id => id.toString());
      
      // Route to the correct backend based on original search type
      if (lastSearchParams?.searchType === 'quick-discover') {
        devLog('🔄 Refresh → quick-discover with stored params');
        const { data, error } = await supabase.functions.invoke('quick-discover', {
          body: {
            moods,
            mediaType,
            obscurity: lastSearchParams.obscurity ?? null,
            era: lastSearchParams.era ?? null,
            excludeIds,
            isPremium,
            reroll: true,
            userId: user?.id,
            keywords: lastSearchParams.keywords,
            genres,
            runtime,
            language,
          }
        });

        if (error || !data?.results?.length) {
          toast({ title: "No new results", description: "Try adjusting the filters", variant: "destructive" });
          return;
        }

        setMovies(data.results);
        const newIds = data.results.map((m: TMDBResult) => m.id);
        setShownMovieIds(prev => [...prev, ...newIds]);
        toast({ title: "✨ New Recommendations", description: `Found ${data.results.length} new picks` });
      } else {
        devLog('🔄 Refresh → ai-search with stored params');
        const { data, error } = await supabase.functions.invoke('ai-search', {
          body: { 
            query,
            isPremium,
            userId: user?.id,
            excludeIds,
            mediaType,
            preferredModel: selectedAIModel,
            purpose: 'refresh',
            moods,
            genres,
            runtime,
            language,
          }
        });

        if (error || data?.error) {
          toast({ title: "Refresh Failed", description: "Failed to get new recommendations.", variant: "destructive" });
          return;
        }

        if (data.results?.length > 0) {
          setMovies(data.results);
          const newIds = data.results.map((m: TMDBResult) => m.id);
          setShownMovieIds(prev => [...prev, ...newIds]);
          toast({ title: "✨ New Recommendations", description: `Found ${data.results.length} new personalized recommendations` });
        } else {
          toast({ title: "No new results", description: "Try a different search query" });
        }
      }
    } catch (error) {
      console.error('Refresh error:', error);
      toast({ title: "Refresh Error", description: "Failed to refresh. Please try again.", variant: "destructive" });
    } finally {
      setIsSearching(false);
      setIsLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (!user) {
      requestTicket('load more picks');
      return;
    }
    if (!searchQuery.trim()) return;
    
    setIsLoadingMore(true);
    
    try {
      const excludeIds = shownMovieIds.map(id => id.toString());
      const query = lastSearchParams?.query || searchQuery;
      const mediaType = lastSearchParams?.mediaType || searchMediaType;
      const moods = lastSearchParams?.moods || [];
      const genres = lastSearchParams?.genres || [];
      const runtime = lastSearchParams?.runtime;
      const language = lastSearchParams?.language;
      
      let resultsArray: TMDBResult[] = [];

      // Route to the correct backend based on original search type
      if (lastSearchParams?.searchType === 'quick-discover') {
        devLog('📥 Load more → quick-discover with stored params');
        const { data, error } = await supabase.functions.invoke('quick-discover', {
          body: {
            moods,
            mediaType,
            obscurity: lastSearchParams.obscurity ?? null,
            era: lastSearchParams.era ?? null,
            excludeIds,
            isPremium,
            userId: user?.id,
            keywords: lastSearchParams.keywords,
            genres,
            runtime,
            language,
          }
        });

        if (!error && data?.results?.length) {
          resultsArray = data.results;
        }
      } else {
        devLog('📥 Load more → ai-search with stored params');
        const { data, error } = await supabase.functions.invoke('ai-search', {
          body: { 
            query,
            isPremium,
            userId: user?.id,
            excludeIds,
            mediaType,
            preferredModel: selectedAIModel,
            purpose: 'load_more',
            moods,
            genres,
            runtime,
            language,
          }
        });

        if (!error && !data?.error && data?.results?.length) {
          resultsArray = data.results;
        }
      }

      if (resultsArray.length > 0) {
        setMovies(prev => [...prev, ...resultsArray]);
        const newIds = resultsArray.map((m: TMDBResult) => m.id);
        setShownMovieIds(prev => [...prev, ...newIds]);
        toast({ title: "✨ More Recommendations Loaded", description: `Added ${resultsArray.length} more picks` });
      } else {
        toast({ title: "No more results", description: "Try adjusting filters or a different query" });
      }
    } catch (error) {
      console.error('Load more error:', error);
      toast({ title: "Error", description: "Failed to load more. Please try again.", variant: "destructive" });
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Legacy toggle handler removed — WatchStatusSelector + realtime subscription handles everything now.

  const handleSpin = () => {
    analytics.track('spin_wheel_used');
    const left = Math.max(0, freeSpinsLeft - 1);
    setFreeSpinsLeft(left);
    if (!isPremium) {
      try { localStorage.setItem(spinKey, String(FREE_DAILY_PICKS - left)); } catch {}
    }
    toast({
      title: "Movie Selected!",
      description: isPremium
        ? "The wheel has chosen your next movie"
        : left > 0
          ? `${left} free pick${left === 1 ? '' : 's'} left today`
          : "That was your last free pick today — more tomorrow, or go VIP for unlimited.",
    });
  };

  const handleUpgrade = () => {
    window.location.assign('/vip?from=discover');
  };

  const handleFiltersChange = (newFilters: FilterState) => {
    setFilters(newFilters);
    setCurrentPage(1);
    searchMovies(searchQuery, newFilters, 1);
  };

  const hasSearchQuery = searchQuery.trim().length > 0;

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onSearch: () => searchInputRef.current?.focus(),
    onClearFilters: () => handleFiltersChange({ genres: [], yearRange: "all", rating: "all", language: "all", mediaType: "all", runtime: "all" }),
    onNextTab: () => {
      const tabs = ['discover', 'vault', 'random'];
      const currentIndex = tabs.indexOf(activeTab);
      const nextIndex = (currentIndex + 1) % tabs.length;
      setActiveTab(tabs[nextIndex]);
    },
    onPrevTab: () => {
      const tabs = ['discover', 'vault', 'random'];
      const currentIndex = tabs.indexOf(activeTab);
      const prevIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      setActiveTab(tabs[prevIndex]);
    }
  });

  // Watchlist movies now come directly from watchlistItems state (fetched from user_favorites)



  if (showGuestLanding) {
    return (
      <>
        <SEOHead />
        <GuestShowcase />
      </>
    );
  }

  return (
    <>
      <SEOHead />
      <div className="min-h-screen bg-background flex flex-col relative z-10 pb-24 lg:pb-0">
        {/* Hero Section */}
      <section className="relative py-4 sm:py-6 md:py-8 px-3 sm:px-4 text-center overflow-hidden">
        <CinemaBackground />
        <InteractiveStars />
        <div className="relative z-10 max-w-4xl mx-auto space-y-4 sm:space-y-6 md:space-y-8">
          {/* Auth Status Bar */}
          <div className="flex justify-between items-center gap-2 sm:gap-3 flex-wrap">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShareOpen(true)}
              className="text-foreground/70 hover:text-foreground"
            >
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </Button>
            
            <div className="flex items-center gap-1.5 sm:gap-3">
              {user ? (
                <>
                  {isOwner && (
                    <Link to="/admin">
                      <Button variant="outline" size="sm" className="gap-2">
                        <ShieldCheck className="h-4 w-4" />
                        Backstage
                      </Button>
                    </Link>
                  )}

                  <div className="flex items-center gap-1.5 sm:gap-4">
                    <Suspense fallback={null}>
                      <SubscriptionManager
                        userId={user.id}
                        onSubscriptionChange={refetchPremium}
                        open={subscriptionOpen}
                        onOpenChange={setSubscriptionOpen}
                      />
                    </Suspense>
                    <Link to="/profile">
                      <Button 
                        data-tutorial="profile-button"
                        variant="ghost" 
                        size="sm" 
                        className="text-foreground/70 hover:text-foreground px-2 sm:px-3 min-h-[40px]"
                      >
                        <User className="h-4 w-4 sm:mr-2" />
                        <span className="hidden sm:inline">Profile</span>
                      </Button>
                    </Link>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={signOut}
                      className="text-foreground/70 hover:text-foreground px-2 sm:px-3 min-h-[40px] text-xs sm:text-sm"
                    >
                      <span className="hidden sm:inline">Sign Out</span>
                      <LogIn className="h-4 w-4 sm:hidden rotate-180" />
                    </Button>
                  </div>
                </>
              ) : (
                <Link to="/auth">
                  <Button variant="ghost" size="sm" className="text-foreground/70 hover:text-foreground">
                    <LogIn className="h-4 w-4 mr-2" />
                    Sign In
                  </Button>
                </Link>
              )}
            </div>
          </div>

          <div className="space-y-4 sm:space-y-6">
            <div className="flex items-center justify-between">
              <button 
                onClick={() => {
                  if (!user) {
                    try { sessionStorage.removeItem("swiftix_guest_browsing"); } catch { /* private mode */ }
                    setGuestBrowsing(false);
                    window.scrollTo({ top: 0 });
                    return;
                  }
                  setSearchQuery('');
                  setShownMovieIds([]);
                  searchMovies('', filters);
                }}
                className="hover:opacity-80 transition-opacity cursor-pointer"
              >
                <Logo size="lg" />
              </button>
              <div className="flex items-center gap-1.5 sm:gap-2" />
            </div>

            {false && user && !hasSearchQuery && (
              <PersonalizedHero
                userName={userName}
                searchCount={searchCount}
                isPremium={isPremium}
                onUpgrade={handleUpgrade}
              />
            )}

            {/* Movie Night CTA moved into the Community hub (Movie Night tab). */}

          </div>

          {/* Quick Discovery - New mood-based UI */}
          {activeTab === 'discover' && (
          <div className="relative space-y-4">
            <button
              type="button"
              onClick={() => openManualSearch()}
              aria-label="Manual Search — look up a movie or show by name"
              className="group flex w-full items-center gap-3 rounded-2xl border border-marquee-bulb/40 bg-background/60 p-4 text-left backdrop-blur transition-all hover:border-marquee-bulb/70 hover:bg-background/80 sm:gap-4 sm:p-5"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-marquee-bulb/40 bg-marquee-bulb/10 text-marquee-bulb sm:h-12 sm:w-12">
                <Search className="h-5 w-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-base font-bold tracking-wide sm:text-lg">Manual Search</span>
                <span className="block truncate text-xs text-muted-foreground sm:text-sm">
                  Know the title already? Search any movie or show by name.
                </span>
              </span>
              <span className="hidden shrink-0 rounded-md border border-border/60 px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground sm:inline">
                Press /
              </span>
            </button>
            <div className="flex flex-wrap items-center justify-center gap-3 sm:justify-end">
              <InlineFeedback
                variant={0}
                label="Noticed a bug? Send feedback"
                className="sm:min-w-[320px]"
              />
            </div>
            <QuickDiscovery 
              onDiscover={handleQuickDiscover}
              isLoading={isSearching}
              isPremium={isPremium}
            />
          </div>
          )}
        </div>
      </section>



      {/* Main Content */}
      <section className="py-4 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Enhanced Loading State - Uses skeleton grid to prevent layout shift */}
          {isSearching && (
            <div className="space-y-8">
              {/* Skeleton header */}
              <div className="text-center space-y-3">
                <div className="flex items-center justify-center gap-2">
                  <Sparkles className="h-6 w-6 text-primary animate-pulse" />
                  <h2 className="text-lg sm:text-2xl font-bold">Curating tonight's picks…</h2>
                </div>
                <p className="text-muted-foreground">Setting the scene for your screening</p>
              </div>
              
              {/* Skeleton grid matching actual layout */}
              <div className={`grid gap-3 sm:gap-6 ${
                viewMode === 'grid' 
                  ? 'grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4' 
                  : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5'
              }`}>
                {Array.from({ length: viewMode === 'compact' ? 15 : 8 }).map((_, i) => (
                  <Card key={i} className="overflow-hidden animate-pulse">
                    <div className="aspect-[2/3] bg-gradient-to-br from-muted via-muted/80 to-muted animate-shimmer" />
                    <CardContent className="p-4 space-y-3">
                      <div className="h-4 bg-muted rounded w-4/5" />
                      <div className="h-3 bg-muted rounded w-1/2" />
                      <div className="flex gap-2">
                        <div className="h-5 bg-muted rounded-full w-16" />
                        <div className="h-5 bg-muted rounded-full w-12" />
                      </div>
                      <div className="space-y-1">
                        <div className="h-2 bg-muted rounded w-full" />
                        <div className="h-2 bg-muted rounded w-3/4" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          <div className={isSearching ? "hidden" : ""}>
            <NowPlayingMarquee />
            <Tabs defaultValue="tonight" value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6 mt-4">


            {(
              <div className="w-full max-w-3xl mx-auto">

                <div className="film-strip-top film-strip-bottom bg-velvet rounded-xl p-0.5">
                  <TabsList
                    data-tutorial="main-tabs"
                    className="w-full h-auto p-1 grid grid-cols-5 gap-1 bg-transparent border-0 rounded-lg"
                  >
                    <TabsTrigger value="tonight" data-tutorial="tab-tonight" className="tab-ticket flex-col gap-1.5 py-4 min-h-[72px]">
                      <Home className="h-5 w-5" />
                      <span className="text-[11px] sm:text-[13px] font-semibold">Tonight</span>
                    </TabsTrigger>
                    <TabsTrigger value="discover" data-tutorial="tab-discover" className="tab-ticket flex-col gap-1.5 py-4 min-h-[72px]">
                      <TrendingUp className="h-5 w-5" />
                      <span className="text-[11px] sm:text-[13px] font-semibold">Discover</span>
                    </TabsTrigger>
                    <TabsTrigger value="analyze" data-tutorial="tab-analyze" className="tab-ticket flex-col gap-1.5 py-4 min-h-[72px]">
                      <Sparkles className="h-5 w-5" />
                      <span className="text-[11px] sm:text-[13px] font-semibold">Analyze</span>
                    </TabsTrigger>
                    <TabsTrigger value="vault" data-tutorial="tab-vault" className="tab-ticket flex-col gap-1.5 py-4 min-h-[72px]">
                      <Gem className="h-5 w-5" />
                      <span className="text-[11px] sm:text-[13px] font-semibold">Vault</span>
                    </TabsTrigger>
                    <TabsTrigger value="random" data-tutorial="tab-random" className="tab-ticket flex-col gap-1.5 py-4 min-h-[72px]">
                      <Dices className="h-5 w-5" />
                      <span className="text-[11px] sm:text-[13px] font-semibold">Random</span>
                    </TabsTrigger>
                  </TabsList>

                </div>
              </div>
            )}

            <TabsContent value="tonight" className="space-y-8">
              <Suspense fallback={<TabChunkFallback />}>
                <TonightTab
                  userId={user?.id}
                  displayName={(user?.user_metadata as Record<string, unknown> | undefined)?.full_name as string | undefined ?? user?.email}
                  isPremium={isPremium}
                  watchlistItems={watchlistItems}
                  onOpenSearch={() => setActiveTab('discover')}
                  onOpenManualSearch={() => openManualSearch()}
                  onUpgrade={handleUpgrade}
                />
              </Suspense>
            </TabsContent>



            <TabsContent value="analyze" className="space-y-8">
              <Suspense fallback={<TabChunkFallback />}>

              <div className="flex justify-center sm:justify-end">
                <InlineFeedback
                  variant={1}
                  label="Got a suggestion for analysis?"
                  className="sm:min-w-[320px]"
                />
              </div>
              <MovieAnalysis />
              </Suspense>
            </TabsContent>

            <TabsContent value="vault" className="space-y-8">
              <Suspense fallback={<TabChunkFallback />}>
              {/* Cinema Vault - Curated Hidden Gems by Genre */}
              <div className="flex justify-center sm:justify-end">
                <InlineFeedback
                  variant={2}
                  label="Something feel off? Let us know"
                  className="sm:min-w-[320px]"
                />
              </div>
              <CinemaVault
                userId={user?.id}
                isPremium={isPremium}
                onUpgrade={handleUpgrade}
                collections={userCollections}
              />
              </Suspense>
            </TabsContent>

            <TabsContent value="discover" className="space-y-8">
                {/* Quick Discovery is visible to everyone; actions inside are ticket-gated. */}
                {(

                  <>
                    {!hasSearchQuery && (
                      <>
                        {/* Feeling Lucky CTA + Browse Controls */}
                        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                          <div className="space-y-3">
                            <div>
                              <h2 className="text-xl sm:text-3xl font-bold">Browse Movies</h2>
                              <p className="text-muted-foreground">
                                Explore our complete catalog
                              </p>
                            </div>
                            <InlineFeedback
                              variant={3}
                              label="Have an idea? Tell us"
                              className="sm:min-w-[320px]"
                            />
                          </div>
                          <div className="flex items-center gap-3 self-start flex-wrap">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setActiveTab('random')}
                              className="border-accent/40 text-accent hover:bg-accent/10 hover:border-accent/60"
                            >
                              <Dices className="h-4 w-4 mr-1.5" />
                              Feeling Lucky?
                            </Button>
                          </div>
                        </div>
                      </>
                    )}


                    {/* Search Results Header */}
                    {(hasSearchQuery || filters.genres.length > 0 || filters.yearRange !== 'all' || filters.rating !== 'all' || filters.language !== 'all' || filters.mediaType !== 'all' || filters.runtime !== 'all') && (
                      <div className="text-center space-y-2">
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
                          <h2 className="text-lg sm:text-2xl font-bold text-center">
                            {hasSearchQuery ? `Picks for "${searchQuery}"` : "Filtered Results"}
                          </h2>
                          {hasSearchQuery && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleRefreshAISearch}
                              disabled={isLoading}
                              className="min-h-[44px] sm:min-h-0"
                            >
                              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                              Refresh
                            </Button>
                          )}
                        </div>
                        <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground flex-wrap">
                          <span>Found {Array.isArray(movies) ? movies.length : 0} perfect matches</span>
                          {Array.isArray(movies) && movies.length > 0 && (
                            <div className="flex items-center gap-2">
                              <Filter className="h-4 w-4" />
                              <span>Avg Rating: {(movies.reduce((acc, m) => acc + m.rating, 0) / movies.length).toFixed(1)}/10</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Movie Grid */}
                    {isLoading ? (
                      <div className={`grid gap-3 sm:gap-6 ${
                        viewMode === 'grid' 
                          ? 'grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4' 
                          : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5'
                      }`}>
                        {Array.from({ length: viewMode === 'compact' ? 15 : 8 }).map((_, i) => (
                          <Card key={i} className="animate-pulse">
                            <div className="aspect-[2/3] bg-muted"></div>
                            <CardContent className="p-4 space-y-2">
                              <div className="h-4 bg-muted rounded"></div>
                              <div className="h-3 bg-muted rounded w-2/3"></div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : movies.length > 0 ? (
                      <>
                        <div className={`grid gap-3 sm:gap-6 ${
                          viewMode === 'grid' 
                            ? 'grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4' 
                            : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5'
                        }`}>
                          {Array.isArray(movies) && movies.map((movie) => (
                            <div key={movie.id} className="space-y-2">
                              <EnhancedMovieCard
                                id={movie.id}
                                title={movie.title}
                                year={movie.year}
                                overview={movie.overview}
                                poster={movie.poster}
                                rating={movie.rating}
                                media_type={movie.media_type}
                                certification={movie.certification}
                                isPremium={isPremium}
                                userId={user?.id}
                                collections={userCollections}
                                ai_match_score={movie.ai_match_score}
                                pickReason={isPremium ? movie.ai_reason : undefined}
                              />
                            </div>
                          ))}
                        </div>

                        {hasSearchQuery && (
                          <div className="flex justify-center">
                            <PickFeedbackBar
                              userId={user?.id}
                              searchQuery={searchQuery}
                              searchType={lastSearchParams ? 'quick-discover' : 'ai-search'}
                              mediaType={searchMediaType}
                              movies={movies}
                            />
                          </div>
                        )}


                        {/* Post-success upgrade nudge for free users */}
                        {hasSearchQuery && user && (
                          <PostResultsUpgradeBanner
                            isPremium={isPremium}
                            onUpgrade={handleUpgrade}
                            context="search"
                          />
                        )}

                        {/* Load More Button - For AI Search Results */}
                        {hasSearchQuery && (
                          <div className="flex justify-center mt-8">
                            <Button
                              variant="outline"
                              size="lg"
                              onClick={handleLoadMore}
                              disabled={isLoadingMore}
                              className="gap-2"
                            >
                              <RefreshCw className={`h-4 w-4 ${isLoadingMore ? 'animate-spin' : ''}`} />
                              Load More Recommendations
                            </Button>
                          </div>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                          <Pagination className="mt-8">
                            <PaginationContent>
                              <PaginationItem>
                                <PaginationPrevious 
                                  onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                                  className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                />
                              </PaginationItem>
                              
                              {/* First page */}
                              {currentPage > 3 && (
                                <>
                                  <PaginationItem>
                                    <PaginationLink onClick={() => handlePageChange(1)} className="cursor-pointer">
                                      1
                                    </PaginationLink>
                                  </PaginationItem>
                                  {currentPage > 4 && <PaginationEllipsis />}
                                </>
                              )}

                              {/* Pages around current */}
                              {Array.from({ length: 5 }, (_, i) => currentPage - 2 + i)
                                .filter(page => page > 0 && page <= totalPages)
                                .map(page => (
                                  <PaginationItem key={page}>
                                    <PaginationLink 
                                      onClick={() => handlePageChange(page)}
                                      isActive={page === currentPage}
                                      className="cursor-pointer"
                                    >
                                      {page}
                                    </PaginationLink>
                                  </PaginationItem>
                                ))}

                              {/* Last page */}
                              {currentPage < totalPages - 2 && (
                                <>
                                  {currentPage < totalPages - 3 && <PaginationEllipsis />}
                                  <PaginationItem>
                                    <PaginationLink onClick={() => handlePageChange(totalPages)} className="cursor-pointer">
                                      {totalPages}
                                    </PaginationLink>
                                  </PaginationItem>
                                </>
                              )}

                              <PaginationItem>
                                <PaginationNext 
                                  onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                                  className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                />
                              </PaginationItem>
                            </PaginationContent>
                          </Pagination>
                        )}
                      </>
                    ) : (
                      <Card className="p-12 text-center">
                        <div className="space-y-4">
                          <Filter className="h-16 w-16 mx-auto text-muted-foreground" />
                          <div>
                            <h3 className="text-xl font-semibold mb-2">No movies found</h3>
                            <p className="text-muted-foreground">
                              Try adjusting your filters or search terms
                            </p>
                          </div>
                          <Button 
                            variant="outline" 
                            onClick={() => {
                              const resetFilters: FilterState = { genres: [], yearRange: "all", rating: "all", language: "all", mediaType: "all", runtime: "all" };
                              setFilters(resetFilters);
                              setSearchQuery("");
                              searchMovies("", resetFilters);
                            }}
                          >
                            Clear All Filters
                          </Button>
                        </div>
                      </Card>
                    )}

                  </>
                )}
            </TabsContent>

            <TabsContent value="random">
              <div className="space-y-8">
                <div className="text-center space-y-3">
                  <h2 className="text-2xl sm:text-3xl font-bold flex items-center justify-center gap-2">
                    <Dices className="h-7 w-7 text-accent" />
                    Random Pick
                  </h2>
                  <p className="text-muted-foreground max-w-lg mx-auto">
                    Can't decide? Customize your preferences and let us pick something for you.
                  </p>
                  <div className="flex justify-center">
                    <InlineFeedback
                      variant={4}
                      label="How's the random picker? Share your thoughts"
                      className="sm:min-w-[360px]"
                    />
                  </div>
                </div>
                
                {!user ? (
                  <Card className="glass-card p-6 sm:p-12 text-center border-primary/30 max-w-2xl mx-auto">
                    <div className="mx-auto w-20 h-20 rounded-full bg-accent/10 flex items-center justify-center mb-4">
                      <Dices className="h-10 w-10 text-accent" />
                    </div>
                    <h3 className="text-2xl font-semibold mb-3">Random Pick</h3>
                    <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                      Sign in to get personalized random movie and TV show picks based on your mood and preferences!
                    </p>
                    <Link to="/auth">
                      <Button size="lg" className="gap-2">
                        <Sparkles className="h-4 w-4" />
                        Sign In to Spin
                      </Button>
                    </Link>
                  </Card>
                ) : (
                  <Suspense fallback={<TabChunkFallback />}>
                  <EnhancedSpinWheel
                    freeSpinsLeft={freeSpinsLeft}
                    onSpin={handleSpin}
                    onGetMoreSpins={() => window.location.assign('/vip?from=random')}
                    isPremium={isPremium}
                    onUpgrade={handleUpgrade}
                    userId={user?.id}
                    watchlistIds={watchlistTmdbIds}
                    active={activeTab === 'random'}
                  />
                  </Suspense>
                )}
              </div>
            </TabsContent>

            {/* Saved / Watchlist has moved to Profile → Activity */}
          </Tabs>
          </div>
        </div>
      </section>

      {user && (
        <section className="container mx-auto px-4 pb-12">
          <div className="mx-auto max-w-3xl rounded-2xl border border-border/40 bg-card/40 backdrop-blur p-6 sm:p-8 text-center space-y-4">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
              Connect with cinephiles
            </p>
            <h3 className="text-2xl sm:text-3xl font-bold">
              Join the <span className="gradient-text">Swiftix community</span>
            </h3>
            <p className="text-sm text-muted-foreground max-w-xl mx-auto">
              Discuss films, follow people with great taste, and chat with friends about your next watch.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              <GradientActionButton
                to="/community"
                icon={Users}
                eyebrow="Discover people"
                label="Open Community"
                {...BUTTON_PALETTES.community}
              />
              <GradientActionButton
                to="/community?tab=messages"
                icon={MessageSquare}
                eyebrow="Direct chats"
                label="Open Messages"
                {...BUTTON_PALETTES.messages}
              />
            </div>
          </div>
        </section>
      )}

        {/* Footer */}
        <Footer />

      {/* Tutorial is now mounted at App level */}


      {/* Feature Tooltips */}
      <FeatureTooltips />

      {/* First-run guided tour is mounted globally in App.tsx so it survives route changes */}


      {/* Share Dialog */}
      <Suspense fallback={null}>
        <ShareWatchlist
          open={shareOpen}
          onOpenChange={setShareOpen}
          watchlistCount={watchlist.length}
        />
      </Suspense>

        {/* Subscription Manager */}
        <Suspense fallback={null}>
          <SubscriptionManager
            userId={user?.id}
            onSubscriptionChange={refetchPremium}
            open={subscriptionOpen}
            onOpenChange={setSubscriptionOpen}
          />
        </Suspense>
      </div>
    </>
  );
};

export default Index;
